import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import styles from "./styles.module.scss";
import { Auth } from "src/redux/Auth/types";
import { AuthActions } from "src/redux/Auth/actions";
import { ModalActions } from "src/redux/Modal/actions";
import { acceptTerms } from "src/utils/auth";
import CheckBox from "src/components/common/CheckBox";
import Button from "src/components/common/Button";
import RecommendMFA from "src/components/views/Lander/Login/RecommendMFA";

/**
 * The documents a user consents to. One acceptance covers all four, so this is
 * a reading list rather than four separate agreements. Served from the same
 * origin as the app, so a counterparty user reaches them on their own host.
 */
const DOCUMENTS = [
    { label: "Terms of Use (Korean)", href: "/legal/terms_of_use_kor.html" },
    { label: "Terms of Use (English)", href: "/legal/terms_of_use_eng.html" },
    { label: "Privacy Policy (Korean)", href: "/legal/privacy_policy_kor.html" },
    { label: "Privacy Policy (English)", href: "/legal/privacy_policy_eng.html" },
];

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

/**
 * The stored `YYYY-MM-DD` as the documents word it, "September 14, 2026".
 *
 * Formatted here rather than through toLocaleDateString, which renders a
 * different date for every reader: this is the wording of the document the
 * reader is agreeing to, so it must read the same for all of them.
 */
const formatEffective = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
    if (!match) return null;
    const [, year, month, day] = match;
    const name = MONTHS[Number(month) - 1];
    return name ? `${name} ${Number(day)}, ${year}` : null;
};

/**
 * Body of the consent modal. The modal chrome around it (the card, the close
 * control, the backdrop over the landing page) is the app's own, so this is
 * only the contents.
 *
 * Closing signs the user out rather than dismissing: the gateway refuses every
 * UI request until they accept, so there is nothing to return to, and someone
 * who opened the wrong account needs a way back to the sign-in page.
 */
export default () => {
    const dispatch = useDispatch();
    // Stated by the documents themselves and read out of them by auth-gateway,
    // so publishing a revision that moves the date needs no change here.
    const effectiveDate = formatEffective(
        useSelector((state) => state.Auth.termsEffectiveDate)
    );
    const mfaNotice = useSelector((state) => state.Auth.mfaNoticeDeferred);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);


    const handleAccept = async () => {
        setSubmitting(true);
        setError(null);
        const accepted = await acceptTerms();
        if (!accepted) {
            setSubmitting(false);
            setError("Could not record your agreement. Please try again.");
            return;
        }
        dispatch({ type: Auth.TERMS_ACCEPTED });
        dispatch(ModalActions.Close());

        // Terms first, then MFA. A session whose counterparty enforces MFA
        // without an enrolled authenticator cannot boot the app at all - every
        // proxied request 403s until enrolment - so accepting the terms hands
        // straight over to enrolment rather than running the bootstrap. The app
        // boots after enrolment upgrades the session.
        if (mfaNotice?.mfaPending) {
            dispatch({ type: Auth.MFA_NOTICE_SHOWN });
            dispatch(
                ModalActions.Open(RecommendMFA, {
                    width: 650,
                    ignoreClickOutside: true,
                    customProps: { mfaEnforced: mfaNotice.mfaEnforced },
                })
            );
            return;
        }

        // The bootstrap was skipped while the gate was closed. Run the same
        // sequence CompleteLogin would have, INIT_COMPLETE included: it is half
        // of the app-ready signal, and without it the app never reports ready.
        await dispatch(AuthActions.LoggedIn());
        await dispatch(AuthActions.AfterLoggedIn());
        dispatch({ type: Auth.INIT_COMPLETE });

        // The login screen deferred the MFA recommendation because this gate
        // stood in front of it. Raise it now, after the fetches that would have
        // closed it have run.
        if (mfaNotice) {
            dispatch({ type: Auth.MFA_NOTICE_SHOWN });
            dispatch(
                ModalActions.Open(RecommendMFA, {
                    width: 650,
                    ignoreClickOutside: true,
                    customProps: { mfaEnforced: mfaNotice.mfaEnforced },
                })
            );
        }
    };

    return (
        <div className={styles.terms} data-testid="terms-gate">
            <div className={styles.title}>Terms of Use &amp; Privacy Policy</div>
            {effectiveDate && (
                <div className={styles.effective}>Effective {effectiveDate}</div>
            )}
            <div className={styles.intro}>
                Please review the updated documents below. You must agree to
                continue using the service.
            </div>

            <div className={styles.documents}>
                {DOCUMENTS.map((doc) => (
                    <a
                        key={doc.href}
                        className={styles.document}
                        href={doc.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`terms-doc-${doc.href}`}
                    >
                        <span>{doc.label}</span>
                        <span className={styles.external} aria-hidden="true" />
                    </a>
                ))}
            </div>

            <div className={styles.agree}>
                <CheckBox
                    name="terms"
                    selected={agreed}
                    disabled={submitting}
                    onChange={() => setAgreed(!agreed)}
                    dataTestId="terms-agree"
                    small
                    styleName="mint"
                    ariaLabel="Agree to the terms of use and privacy policy"
                >
                    I have read and agree to the Terms of Use and Privacy
                    Policy.
                </CheckBox>
            </div>

            {error && (
                <div className={styles.error} data-testid="terms-error">
                    {error}
                </div>
            )}

            <div className={styles.footer}>
                <Button
                    value={submitting ? "Saving…" : "Agree and continue"}
                    disabled={!agreed || submitting}
                    onClick={handleAccept}
                    dataTestId="terms-continue"
                />
            </div>
        </div>
    );
};
